import { AccessorFnColumnDef } from "@tanstack/react-table";
import forEach from "lodash/forEach";
import range from "lodash/range";
import zipObject from "lodash/zipObject";
import { ActivityRow } from "PFApp/activities/engagement_blueprint/components/availability_table";
import {
  FIELD_AVAILABILITY_MODE_KEY,
  FIELD_CONVERTED_AVAILABILITY_KEY,
  FIELD_CONVERTED_AVAILABILITY_RANGES_KEY,
  FIELD_ID_KEY,
  FIELD_UNIQUE_ID_KEY
} from "PFApp/constants/activity_form/activity_form_field_keys";
import { useManageRolesStore } from "PFApp/stores/manage_roles";
import {
  ConvertedRangesFieldKey,
  RoleFormKey,
  RolesFormKey
} from "PFApp/types/activity_form/activity_form_schema";
import { ManageRolesMultiForm } from "PFApp/types/activity_form/manage_roles_form_schema";
import { Inline, Stack } from "PFComponents/containers";
import { HorizontalScrollbar } from "PFComponents/horizontal_scrollbar";
import { AvailabilityMode, StandardRangeV2, Subtemplate } from "PFCore/types";
import { CSSProperties, memo, RefCallback, useCallback, useId, useMemo, useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { AvailabilityTable } from "../../engagement_blueprint/components/availability_table";
import { useManageRolesActionsColumn } from "../hooks/use_manage_roles_actions_column";
import { useVisibilityLimitedCalendarDuration } from "../hooks/use_visibility_limited_calendar_duration";
import { ManageRolesActionType } from "../manage_roles.types";
import { getLastNewRoleRowIndex, getRoleKey } from "../manage_roles.utils";
import { wrapColumnsWithHighlight } from "../manage_roles_cell_highlight";
import css from "../manage_roles_page.module.scss";
import cellCss from "./cell.module.scss";
import { ManageRolesForm } from "./manage_roles_form";

const STICKY_SCROLLBAR_STYLE: CSSProperties = { position: "sticky", bottom: 0 };

type ManageRolesTablesProps = {
  rolesFormKey: RolesFormKey;
  subtemplate: Subtemplate | null;
  leftPanelRef: RefCallback<HTMLDivElement>;
  onEditAvailability: (roleFormKey: RoleFormKey) => void;
  onPublish: (roleFormKey: RoleFormKey) => void;
  onFillAndBook: (roleFormKey: RoleFormKey) => void;
};

const MemoizedManageRolesForm = memo(ManageRolesForm);

const mapAvailabilityColumns = (columns: AccessorFnColumnDef<ActivityRow, any>[]) =>
  wrapColumnsWithHighlight(
    columns,
    (_rowIndex, row) => row.roleKey,
    () => ({ name: FIELD_CONVERTED_AVAILABILITY_KEY })
  );

/**
 * Memoized component holding the two expensive tables.
 * Isolated from EditAvailabilitySidePanel state so opening the panel
 * does not re-render ManageRolesForm or AvailabilityTable.
 */
export const ManageRolesTables = memo(
  ({
    rolesFormKey,
    subtemplate,
    leftPanelRef,
    onEditAvailability,
    onPublish,
    onFillAndBook
  }: ManageRolesTablesProps) => {
    const leftPanelElementRef = useRef<HTMLDivElement>(null);
    const rightPanelRef = useRef<HTMLDivElement>(null);
    const leftPanelId = useId();
    const rightPanelId = useId();
    const { setValue } = useFormContext<ManageRolesMultiForm>();
    const calendarDuration = useVisibilityLimitedCalendarDuration();

    const rolesLength = useWatch<ManageRolesMultiForm, RolesFormKey, unknown, number>({
      name: rolesFormKey,
      compute: (roles = []) => roles.length
    });

    const roleFormKeys = useMemo<RoleFormKey[]>(
      () => range(rolesLength).map((index) => `${rolesFormKey}.${index}` as RoleFormKey),
      [rolesLength, rolesFormKey]
    );

    const rangesValues = useWatch<ManageRolesMultiForm>({
      name: roleFormKeys.map(
        (key) => `${key}.${FIELD_CONVERTED_AVAILABILITY_RANGES_KEY}`
      ) as ConvertedRangesFieldKey[]
    }) as StandardRangeV2[][];

    const availabilityModes = useWatch<ManageRolesMultiForm>({
      name: roleFormKeys.map(
        (key) => `${key}.${FIELD_AVAILABILITY_MODE_KEY}`
      ) as `${RoleFormKey}.${typeof FIELD_AVAILABILITY_MODE_KEY}`[]
    }) as AvailabilityMode[];

    const availabilityTableValue = useMemo(
      () => zipObject(roleFormKeys, rangesValues) as Record<RoleFormKey, StandardRangeV2[]>,
      [roleFormKeys, rangesValues]
    );

    const disabledRoleFormKeys = useMemo(
      () =>
        roleFormKeys.filter(
          (_, index) => availabilityModes[index] !== AvailabilityMode.PerWeek
        ) as RoleFormKey[],
      [roleFormKeys, availabilityModes]
    );

    const roleIds = useWatch<ManageRolesMultiForm>({
      name: roleFormKeys.map((key) => `${key}.${FIELD_ID_KEY}`) as `${RoleFormKey}.${typeof FIELD_ID_KEY}`[]
    }) as (number | undefined)[];

    const unknownAvailabilityRoleFormKeys = useMemo(
      () =>
        roleFormKeys.filter(
          (_, index) => availabilityModes[index] !== AvailabilityMode.PerWeek && !roleIds[index]
        ) as RoleFormKey[],
      [roleFormKeys, availabilityModes, roleIds]
    );

    const roleUniqueIds = useWatch<ManageRolesMultiForm>({
      name: roleFormKeys.map(
        (key) => `${key}.${FIELD_UNIQUE_ID_KEY}`
      ) as `${RoleFormKey}.${typeof FIELD_UNIQUE_ID_KEY}`[]
    }) as string[];

    const roleKeys = useMemo(
      () =>
        roleFormKeys.map((_, index) => getRoleKey({ id: roleIds[index], uniqueId: roleUniqueIds[index] })),
      [roleFormKeys, roleIds, roleUniqueIds]
    );

    const newActivityIds = useManageRolesStore((state) => state.newActivityIds);

    const lastNewRowIndex = useMemo(
      () => getLastNewRoleRowIndex(roleKeys, newActivityIds),
      [roleKeys, newActivityIds]
    );

    const getRowClassName = useCallback(
      (_row: unknown, rowIndex: number) =>
        rowIndex === lastNewRowIndex && rowIndex < roleFormKeys.length - 1
          ? cellCss.newRoleBoundary
          : undefined,
      [lastNewRowIndex, roleFormKeys.length]
    );

    const handleAvailabilityChange = useCallback(
      (availability: Record<RoleFormKey, StandardRangeV2[]>) => {
        forEach(availability, (availabilityRules, roleFormKey) => {
          const fieldPath =
            `${roleFormKey}.${FIELD_CONVERTED_AVAILABILITY_RANGES_KEY}` as ConvertedRangesFieldKey;
          setValue(fieldPath, availabilityRules, { shouldDirty: true });
        });
      },
      [setValue]
    );

    const handleActionsChange = useCallback(
      (roleFormKey: RoleFormKey, action: ManageRolesActionType) => {
        if (action === ManageRolesActionType.EditAvailability) {
          onEditAvailability(roleFormKey);
        } else if (action === ManageRolesActionType.Publish) {
          onPublish(roleFormKey);
        } else if (action === ManageRolesActionType.FillAndBook) {
          onFillAndBook(roleFormKey);
        }
      },
      [onEditAvailability, onPublish, onFillAndBook]
    );

    const generateActionsColumn = useManageRolesActionsColumn<ActivityRow>({
      onChange: handleActionsChange
    });

    return (
      <Inline className={css.accordionContent} gap={null}>
        {/* Plain div, not Stack: needed for the scrollbar to work correctly, see `leftPanelRef`. */}
        <div ref={leftPanelRef} className={css.leftPanelWrapper}>
          <div
            ref={leftPanelElementRef}
            id={leftPanelId}
            className={css.leftPanel}
            data-qa-id={`ManageRoles.${subtemplate?.key}.LeftPanel`}
          >
            <MemoizedManageRolesForm
              subtemplate={subtemplate}
              rolesFormKey={rolesFormKey}
              getRowClassName={getRowClassName}
            />
          </div>
          <HorizontalScrollbar
            scrollableContainerRef={leftPanelElementRef}
            contentRef={leftPanelElementRef}
            getContentWidth={() => leftPanelElementRef.current?.scrollWidth ?? 0}
            getWindowWidth={() => leftPanelElementRef.current?.clientWidth ?? 0}
            style={STICKY_SCROLLBAR_STYLE}
          />
        </div>
        <Stack gap={null} className={css.rightPanelWrapper}>
          <div
            ref={rightPanelRef}
            id={rightPanelId}
            className={css.rightPanel}
            data-qa-id={`ManageRoles.${subtemplate?.key}.RightPanel`}
          >
            <AvailabilityTable
              roleFormKeys={roleFormKeys}
              roleKeys={roleKeys}
              value={availabilityTableValue}
              onRowsChange={handleAvailabilityChange}
              generateActionsColumn={generateActionsColumn}
              mapColumns={mapAvailabilityColumns}
              getRowClassName={getRowClassName}
              scrollableContainerRef={rightPanelRef}
              calendarDuration={calendarDuration}
              disabledRoleFormKeys={disabledRoleFormKeys}
              unknownAvailabilityRoleFormKeys={unknownAvailabilityRoleFormKeys}
            />
          </div>
          <HorizontalScrollbar
            scrollableContainerRef={rightPanelRef}
            contentRef={rightPanelRef}
            getContentWidth={() => rightPanelRef.current?.scrollWidth ?? 0}
            getWindowWidth={() => rightPanelRef.current?.clientWidth ?? 0}
            style={STICKY_SCROLLBAR_STYLE}
          />
        </Stack>
      </Inline>
    );
  }
);

ManageRolesTables.displayName = "ManageRolesTables";
